import { forwardRef, useRef, type InputHTMLAttributes } from 'react';
import { CalendarIcon } from './CalendarIcon';

export type NativeDateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  wrapperClassName?: string;
  iconClassName?: string;
};

export const NativeDateInput = forwardRef<HTMLInputElement, NativeDateInputProps>(
  ({ className = '', wrapperClassName = '', iconClassName = 'h-5 w-5', disabled, ...rest }, forwardedRef) => {
    const innerRef = useRef<HTMLInputElement>(null);

    const setInputRef = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
        return;
      }
      if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    const handleOpenPicker = () => {
      const inputEl = innerRef.current;
      if (!inputEl || disabled) return;
      if (typeof inputEl.showPicker === 'function') {
        try {
          inputEl.showPicker();
          return;
        } catch {
          // Fall through when showPicker is blocked by the browser.
        }
      }
      inputEl.focus();
      inputEl.click();
    };

    const handleOpenPickerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenPicker();
      }
    };

    return (
      <div className={['relative min-w-0', wrapperClassName].filter(Boolean).join(' ')}>
        <input
          ref={setInputRef}
          type="date"
          disabled={disabled}
          className={['fdh-native-date-input pr-10', className].filter(Boolean).join(' ')}
          {...rest}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-hidden
          onClick={handleOpenPicker}
          onKeyDown={handleOpenPickerKeyDown}
          className="pointer-events-auto absolute right-2 top-1/2 z-[1] flex -translate-y-1/2 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CalendarIcon className={iconClassName} />
        </button>
      </div>
    );
  },
);

NativeDateInput.displayName = 'NativeDateInput';
